/**
 * PreviewFilterMenuPanel — the popover body: every preview list control,
 * stacked, plus a reset that returns the list to "show everything".
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { forwardRef } from 'react';
import type { FlowOptimizerPool } from '@trafficpulse/shared';

import PreviewListControlGroup from '../PreviewListControlGroup';
import PreviewPoolSelect from '../PreviewPoolSelect';
import {
  OUTCOME_OPTIONS,
  RANK_OPTIONS,
  SORT_OPTIONS,
  STATUS_OPTIONS,
} from './preview-filter-options';
import type { PreviewFilterMenuProps } from './preview-filter-menu.types';

export interface PreviewFilterMenuPanelProps extends PreviewFilterMenuProps {
  style: { top: number; left: number; width: number };
  onReset: () => void;
  activeCount: number;
  pools?: ReadonlyArray<FlowOptimizerPool>;
}

const PreviewFilterMenuPanel = forwardRef<
  HTMLDivElement,
  PreviewFilterMenuPanelProps
>(function PreviewFilterMenuPanel(props, ref) {
  const { style, onReset, activeCount, pools } = props;
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Preview list filters"
      // pointer-events-auto is REQUIRED, not cosmetic: the optimizer drawer sets
      // `pointer-events: none` on <body> while it is open, and this panel is
      // portaled to <body>, so without it every control renders but no real
      // mouse event ever reaches them (the pool <select> in particular). The
      // hover card carries the same opt-in for the same reason.
      className="pointer-events-auto fixed z-[9999] rounded-lg border border-border/70 bg-popover p-2.5 text-popover-foreground shadow-xl ring-1 ring-black/5"
      style={style}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Filters
        </span>
        <button
          type="button"
          onClick={onReset}
          disabled={activeCount === 0}
          className="rounded px-1.5 py-0.5 text-[10px] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          Reset
        </button>
      </div>
      <div className="space-y-2">
        <PreviewListControlGroup
          label="Sort"
          options={SORT_OPTIONS}
          value={props.sortMode}
          onChange={props.onSortModeChange}
        />
        <PreviewListControlGroup
          label="Rank"
          options={RANK_OPTIONS}
          value={props.rankFilter}
          onChange={props.onRankFilterChange}
        />
        <PreviewListControlGroup
          label="Status"
          options={STATUS_OPTIONS}
          value={props.statusFilter}
          onChange={props.onStatusFilterChange}
        />
        <PreviewListControlGroup
          label="Outcome"
          options={OUTCOME_OPTIONS}
          value={props.outcomeFilter}
          onChange={props.onOutcomeFilterChange}
        />
        {pools && props.onPoolFilterChange ? (
          <PreviewPoolSelect
            pools={pools}
            value={props.poolFilter ?? null}
            onChange={props.onPoolFilterChange}
          />
        ) : null}
      </div>
    </div>
  );
});

export default PreviewFilterMenuPanel;
